import { Ref, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Form, Nav } from 'react-bootstrap';
import { MdExpandLess, MdExpandMore, MdWarningAmber } from 'react-icons/md';
import { ColumnFilterPopover } from '@Components/ColumnFilterPopover';
import { InteractiveTr } from '@Components/InteractiveEl';
import { TableEmptyState } from '@Components/TableEmptyState';
import { searchItems } from '@src/utils/search';
import { MissingDeclaredFeaturesAlert } from '../components/MissingDeclaredFeaturesAlert';
import { SankeyChart } from '../components/SankeyChart';
import { SectionHeader } from '../components/SectionHeader';
import { FeatureMappingRow, MmmConfig, RawComponentRow, UnmappedFeature } from '../data-qa-types';
import { modelTypeForProductType } from '../data-qa-utils';
import {
  distinctColumnValues,
  emptyFacetSelection,
  FacetSelection,
  FilterableColumn,
  filterRowsByFacets
} from './feature-mapping-filter-utils';
import { activeShownGroup, groupsToShow } from './feature-mapping-group-utils';
import {
  buildFeatureSummaryRows,
  buildNotInModelRows,
  FeatureSummaryRow
} from './feature-mapping-summary-utils';

const RAW_COMPONENTS_PAGE_SIZE = 10;
const RAW_COMPONENTS_COLUMN_COUNT = 6;

const RAW_COMPONENT_COLUMN_LABEL: Record<FilterableColumn, string> = {
  source: 'Source',
  channel: 'Channel',
  platform: 'Platform',
  tactic: 'Tactic'
};

// Mirrors CampaignsSection's FacetColumnHeader, scoped to RawComponentRow -- same
// ColumnFilterPopover, same "counts against the full un-filtered set" semantics.
const RawComponentsFacetHeader = ({
  column,
  rows,
  selection,
  onFacetChange
}: {
  column: FilterableColumn;
  rows: RawComponentRow[];
  selection: FacetSelection;
  onFacetChange: (column: FilterableColumn, next: ReadonlySet<string>) => void;
}) => (
  <th>
    <div className='data-qa-fm-table__col-header'>
      <span className='data-qa-fm-table__col-label'>{RAW_COMPONENT_COLUMN_LABEL[column]}</span>
      <ColumnFilterPopover
        label={RAW_COMPONENT_COLUMN_LABEL[column]}
        options={distinctColumnValues(rows, column)}
        selected={selection[column]}
        onChange={next => onFacetChange(column, next)}
      />
    </div>
  </th>
);

// Mirrors CampaignsSection's TextFilterColumnHeader -- same searchItems-backed free-text
// filter, used for Campaign Name/Campaign ID since those are effectively unique per row.
const RawComponentsTextHeader = ({
  label,
  placeholder,
  query,
  onQueryChange
}: {
  label: string;
  placeholder: string;
  query: string;
  onQueryChange: (query: string) => void;
}) => (
  <th>
    <div className='data-qa-fm-table__col-header'>
      <span className='data-qa-fm-table__col-label'>{label}</span>
      <Form.Control
        className='data-qa-fm-table__col-filter'
        type='search'
        size='sm'
        placeholder={placeholder}
        value={query}
        onChange={e => onQueryChange(e.target.value)}
      />
    </div>
  </th>
);

// Filterable, paginated Raw Components table for one selected feature. Defaults to the
// first RAW_COMPONENTS_PAGE_SIZE (filtered) rows with an expander for the rest, since a
// popular feature can have hundreds of contributing campaigns. Parented with
// key={effectiveSelected} so switching the MMM Feature dropdown resets filters/expansion
// rather than carrying a prior feature's narrowed view into a new one.
const RawComponentsTable = ({ rows }: { rows: RawComponentRow[] }) => {
  const [facetSelection, setFacetSelection] = useState<FacetSelection>(emptyFacetSelection());
  const [campaignQuery, setCampaignQuery] = useState('');
  const [campaignIdQuery, setCampaignIdQuery] = useState('');
  const [expanded, setExpanded] = useState(false);

  const handleFacetChange = (column: FilterableColumn, next: ReadonlySet<string>) => {
    setFacetSelection(prev => ({ ...prev, [column]: next }));
  };

  const filteredRows = useMemo(() => {
    const byFacet = filterRowsByFacets(rows, facetSelection);
    const byCampaign = searchItems(byFacet, campaignQuery, {
      keys: ['campaignName'],
      rank: false
    });
    return searchItems(byCampaign, campaignIdQuery, { keys: ['campaignId'], rank: false });
  }, [rows, facetSelection, campaignQuery, campaignIdQuery]);

  const visibleRows = expanded ? filteredRows : filteredRows.slice(0, RAW_COMPONENTS_PAGE_SIZE);
  const hasMore = filteredRows.length > RAW_COMPONENTS_PAGE_SIZE;

  return (
    <>
      <div className='data-qa-fm-card__caption'>
        Showing {visibleRows.length} of {filteredRows.length} row
        {filteredRows.length === 1 ? '' : 's'}
        {filteredRows.length !== rows.length ? ` (filtered from ${rows.length})` : ''}
      </div>
      <table className='data-qa-fm-table'>
        <thead>
          <tr>
            <RawComponentsFacetHeader
              column='source'
              rows={rows}
              selection={facetSelection}
              onFacetChange={handleFacetChange}
            />
            <RawComponentsFacetHeader
              column='channel'
              rows={rows}
              selection={facetSelection}
              onFacetChange={handleFacetChange}
            />
            <RawComponentsFacetHeader
              column='platform'
              rows={rows}
              selection={facetSelection}
              onFacetChange={handleFacetChange}
            />
            <RawComponentsFacetHeader
              column='tactic'
              rows={rows}
              selection={facetSelection}
              onFacetChange={handleFacetChange}
            />
            <RawComponentsTextHeader
              label='Campaign Name'
              placeholder='Filter campaign name...'
              query={campaignQuery}
              onQueryChange={setCampaignQuery}
            />
            <RawComponentsTextHeader
              label='Campaign ID'
              placeholder='Filter campaign ID...'
              query={campaignIdQuery}
              onQueryChange={setCampaignIdQuery}
            />
          </tr>
        </thead>
        <tbody>
          {visibleRows.length === 0 ? (
            <tr>
              <td colSpan={RAW_COMPONENTS_COLUMN_COUNT}>
                <TableEmptyState message='No raw components match the current filters.' />
              </td>
            </tr>
          ) : (
            visibleRows.map((rc, i) => (
              <tr key={`${rc.campaignId}-${i}`}>
                <td>{rc.source || '—'}</td>
                <td>{rc.channel}</td>
                <td>{rc.platform}</td>
                <td>{rc.tactic}</td>
                <td>{rc.campaignName}</td>
                <td>{rc.campaignId}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      {hasMore && (
        <button
          type='button'
          className='data-qa-fm-table__expand'
          onClick={() => setExpanded(e => !e)}
        >
          {expanded ? <MdExpandLess /> : <MdExpandMore />}
          {expanded ? 'Show fewer rows' : `Show all ${filteredRows.length} rows`}
        </button>
      )}
    </>
  );
};

type ChannelGroup = 'demandGeneration' | 'demandCapture';

// A feature to open the page on -- e.g. from the About tab's feature lists.
export interface FeatureMappingFocus {
  group: ChannelGroup;
  feature: string;
}

interface FeatureMappingSectionProps {
  config: MmmConfig;
  initialFocus?: FeatureMappingFocus;
}
const GROUPS: { key: ChannelGroup; label: string }[] = [
  { key: 'demandGeneration', label: 'Demand Generation' },
  { key: 'demandCapture', label: 'Demand Capture' }
];

interface FeatureGroupPanelProps {
  groupLabel: string;
  rows: FeatureMappingRow[];
  rawComponents: RawComponentRow[];
  unmapped: UnmappedFeature[];
  // Controlled by FeatureMappingGroups, so each group keeps its own selection across tabs.
  selected: string;
  onSelect: (feature: string) => void;
  // The Raw Components title, the Feature Summary's scroll target.
  detailTitleRef: Ref<HTMLHeadingElement>;
}

// One group's data flow: its Sankey, a feature picker, that feature's raw components, and
// any declared features without mapping data.
const FeatureGroupPanel = ({
  groupLabel,
  rows,
  rawComponents,
  unmapped,
  selected,
  onSelect,
  detailTitleRef
}: FeatureGroupPanelProps) => {
  const features = useMemo(() => [...new Set(rows.map(r => r.feature))].sort(), [rows]);
  const effectiveSelected = features.includes(selected) ? selected : (features[0] ?? '');
  const detailRows = rawComponents.filter(rc => rc.feature === effectiveSelected);
  // One row per distinct `feature` string (buildFeatureMapping's own grouping, backend)
  // -- looks up the raw feature_id(s) to affix to the detail title below, for
  // debugging (Sean, 2026-09-10).
  const featureIdByFeature = useMemo(
    () => new Map(rows.map(r => [r.feature, r.featureId])),
    [rows]
  );
  const effectiveFeatureId = featureIdByFeature.get(effectiveSelected) || '';
  const selectId = `fm-feature-${groupLabel.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <>
      {rows.length === 0 ? (
        <div className='data-qa-fm-card__empty'>No mapping rows for this group.</div>
      ) : (
        <>
          <SankeyChart rows={rows} height={480} />
          <div className='data-qa-fm-card__selector'>
            <div className='data-qa-fm-card__selector-label'>
              Select a feature to see detailed raw component breakdown:
            </div>
            <label className='data-qa-fm-card__field' htmlFor={selectId}>
              <span>MMM Feature:</span>
              <select
                id={selectId}
                className='data-qa-fm-card__select'
                value={effectiveSelected}
                onChange={e => onSelect(e.target.value)}
              >
                {features.map(f => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <h3 className='data-qa-fm-card__detail-title' ref={detailTitleRef}>
            Raw Components for: {effectiveSelected}
            {effectiveFeatureId && ` | ${effectiveFeatureId}`}
          </h3>
          <RawComponentsTable key={effectiveSelected} rows={detailRows} />
        </>
      )}

      {unmapped.length > 0 && (
        <div className='data-qa-fm-card__unmapped'>
          <h3 className='data-qa-fm-card__detail-title'>
            Defined features without mapping data ({unmapped.length})
          </h3>
          <p className='data-qa-fm-card__caption'>
            Listed in <code>paid_media_exposure_vars</code> but absent from the mapping data —
            they&apos;re counted as defined model features but don&apos;t appear in the Sankey.
          </p>
          <table className='data-qa-fm-table'>
            <thead>
              <tr>
                <th>Feature</th>
                <th>Exposure Variable</th>
              </tr>
            </thead>
            <tbody>
              {unmapped.map(u => (
                <tr key={u.feature}>
                  <td>{u.feature}</td>
                  <td>
                    <code>{u.exposureVariable}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

interface FeatureSummaryColumnProps {
  groupLabel: string;
  rows: FeatureSummaryRow[];
  onFeatureClick: (feature: string) => void;
}

const FeatureSummaryColumn = ({ groupLabel, rows, onFeatureClick }: FeatureSummaryColumnProps) => (
  <div className='data-qa-fm-summary__group'>
    <h3 className='data-qa-fm-summary__group-title'>
      {groupLabel} ({rows.length})
    </h3>
    {rows.length === 0 ? (
      <div className='data-qa-fm-card__empty'>No features in this group.</div>
    ) : (
      <table className='data-qa-fm-table data-qa-fm-summary__table'>
        <thead>
          <tr>
            <th>
              <span className='data-qa-fm-table__col-label'>Feature</span>
            </th>
            <th>
              <span className='data-qa-fm-table__col-label'>Feature ID</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row =>
            row.unmapped ? (
              <tr key={`unmapped-${row.feature || row.featureId}`}>
                <td>
                  {row.feature && <span className='data-qa-fm-summary__name'>{row.feature}</span>}
                  <span className='data-qa-fm-summary__tag'>No mapping data</span>
                </td>
                <td className='data-qa-fm-summary__id'>{row.featureId}</td>
              </tr>
            ) : (
              <InteractiveTr
                key={row.feature}
                className='data-qa-fm-summary__row'
                aria-label={`Show raw components for ${row.feature}`}
                onClick={() => onFeatureClick(row.feature)}
              >
                <td>{row.feature}</td>
                <td className='data-qa-fm-summary__id'>{row.featureId}</td>
              </InteractiveTr>
            )
          )}
        </tbody>
      </table>
    )}
  </div>
);

// Data-frame features this model doesn't declare -- expected when one data frame serves
// several models. Not clickable: the model has no Sankey node or raw components for them.
const NotInModelColumn = ({ rows }: { rows: FeatureSummaryRow[] }) => (
  <div className='data-qa-fm-summary__group'>
    <h3 className='data-qa-fm-summary__group-title'>Not used by this model ({rows.length})</h3>
    <table className='data-qa-fm-table data-qa-fm-summary__table'>
      <thead>
        <tr>
          <th>
            <span className='data-qa-fm-table__col-label'>Feature</span>
          </th>
          <th>
            <span className='data-qa-fm-table__col-label'>Feature ID</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr key={row.featureId} className='data-qa-fm-summary__row--not-in-model'>
            <td>{row.feature || 'Unnamed feature'}</td>
            <td className='data-qa-fm-summary__id'>{row.featureId}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

interface FeatureMappingGroupsProps {
  config: MmmConfig;
  initialFocus?: FeatureMappingFocus;
}

// The Feature Summary plus the data-flow card, which shows one group at a time behind
// Demand Generation / Demand Capture tabs (the same tabs Media Inputs uses for a channel
// split across both groups). Owns the open card, the active tab and each group's selected
// feature, so a summary click can switch to the right tab on that feature. Keyed on
// configPath by its caller: switching models resets this state rather than carrying a
// selection over to another model that happens to share a feature name.
const FeatureMappingGroups = ({ config, initialFocus }: FeatureMappingGroupsProps) => {
  const unmappedFeatures = config.unmappedFeatures ?? [];
  const notInModelRows = buildNotInModelRows(config.excludedFeatures);
  // RawComponentRow has no group of its own -- resolved via its `feature` string,
  // which is built the same way featureMapping's own `.feature` is (see
  // RawComponentRow's doc comment), rather than by `channel` label (see
  // FeatureMappingRow.group's own doc comment for why a channel-label lookup is wrong
  // for a channel that spans both groups).
  const groupByFeature = useMemo(
    () => new Map(config.featureMapping.map(r => [r.feature, r.group])),
    [config.featureMapping]
  );
  const groupData = useMemo(
    () =>
      GROUPS.map(group => ({
        ...group,
        rows: config.featureMapping.filter(r => r.group === group.key),
        rawComponents: (config.rawComponents ?? []).filter(
          rc => groupByFeature.get(rc.feature) === group.key
        ),
        unmapped: (config.unmappedFeatures ?? []).filter(u => u.group === group.key)
      })),
    [config.featureMapping, config.rawComponents, config.unmappedFeatures, groupByFeature]
  );
  const tabGroups = groupsToShow(groupData);

  const [summaryOpen, setSummaryOpen] = useState(true);
  const [flowOpen, setFlowOpen] = useState(true);
  const [activeGroup, setActiveGroup] = useState<ChannelGroup>(
    initialFocus?.group ?? 'demandGeneration'
  );
  const shownGroup = activeShownGroup(tabGroups, activeGroup);
  const [selectedFeatures, setSelectedFeatures] = useState<Record<ChannelGroup, string>>(() => ({
    demandGeneration: '',
    demandCapture: '',
    ...(initialFocus ? { [initialFocus.group]: initialFocus.feature } : {})
  }));
  const detailTitle = useRef<HTMLHeadingElement | null>(null);
  // Set by a summary click (or an initial focus); scrolls once the card has rendered on
  // that feature.
  const [scrollPending, setScrollPending] = useState(Boolean(initialFocus));
  useLayoutEffect(() => {
    if (!scrollPending) return;
    detailTitle.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setScrollPending(false);
  }, [scrollPending]);

  const selectFeature = (group: ChannelGroup, feature: string) =>
    setSelectedFeatures(prev => ({ ...prev, [group]: feature }));
  const showFeature = (group: ChannelGroup, feature: string) => {
    setFlowOpen(true);
    setActiveGroup(group);
    selectFeature(group, feature);
    setScrollPending(true);
  };

  return (
    <>
      <div className='data-qa-fm-card'>
        <button
          type='button'
          className='data-qa-fm-card__head'
          aria-expanded={summaryOpen}
          onClick={() => setSummaryOpen(o => !o)}
        >
          <span className='data-qa-fm-card__title'>Feature Summary</span>
          <span className='data-qa-fm-card__chevron'>
            {summaryOpen ? <MdExpandLess /> : <MdExpandMore />}
          </span>
        </button>
        {summaryOpen && (
          <div className='data-qa-fm-card__body'>
            <p className='data-qa-fm-card__caption'>
              Which model features count as Demand Generation and which as Demand Capture. Select a
              feature to see its raw components.
            </p>
            <div className='data-qa-fm-summary'>
              {GROUPS.map(group => (
                <FeatureSummaryColumn
                  key={group.key}
                  groupLabel={group.label}
                  rows={buildFeatureSummaryRows(group.key, config.featureMapping, unmappedFeatures)}
                  onFeatureClick={feature => showFeature(group.key, feature)}
                />
              ))}
              {notInModelRows.length > 0 && <NotInModelColumn rows={notInModelRows} />}
            </div>
          </div>
        )}
      </div>
      <div className='data-qa-fm-card'>
        <button
          type='button'
          className='data-qa-fm-card__head'
          aria-expanded={flowOpen}
          onClick={() => setFlowOpen(o => !o)}
        >
          <span className='data-qa-fm-card__title'>Data Flow: Raw Components → MMM Features</span>
          <span className='data-qa-fm-card__chevron'>
            {flowOpen ? <MdExpandLess /> : <MdExpandMore />}
          </span>
        </button>
        {flowOpen && (
          <div className='data-qa-fm-card__body'>
            {tabGroups.length > 1 ? (
              <Nav
                variant='pills'
                className='data-qa-channel-tabs'
                activeKey={shownGroup.key}
                onSelect={key => {
                  const group = GROUPS.find(g => g.key === key);
                  if (group) {
                    setActiveGroup(group.key);
                  }
                }}
              >
                {tabGroups.map(group => (
                  <Nav.Item key={group.key}>
                    <Nav.Link eventKey={group.key}>{group.label}</Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            ) : (
              <span className='data-qa-fm-card__group'>{shownGroup.label}</span>
            )}
            <FeatureGroupPanel
              key={shownGroup.key}
              groupLabel={shownGroup.label}
              rows={shownGroup.rows}
              rawComponents={shownGroup.rawComponents}
              unmapped={shownGroup.unmapped}
              selected={selectedFeatures[shownGroup.key]}
              onSelect={feature => selectFeature(shownGroup.key, feature)}
              detailTitleRef={detailTitle}
            />
          </div>
        )}
      </div>
    </>
  );
};

export const FeatureMappingSection = ({ config, initialFocus }: FeatureMappingSectionProps) => {
  const modelType = modelTypeForProductType(config.productType);

  return (
    <div className='data-qa-fm'>
      <SectionHeader
        title='Feature Mapping'
        modelType={modelType}
        subtitle='Visualize how raw data components flow into MMM features. This page is provided for transparency, not formal review.'
        tips={
          <>
            <li>
              <strong>Feature Summary</strong>: Check each feature sits in the right group (Demand
              Generation or Demand Capture)
            </li>
            <li>
              <strong>Flow completeness</strong>: Every raw data source should map to an MMM feature
            </li>
            <li>
              <strong>Unexpected mappings</strong>: Check that channels/platforms flow to the
              correct features
            </li>
            <li>
              <strong>Missing connections</strong>: A feature with no incoming flows means data may
              not be aggregating properly
            </li>
            <li>
              <strong>Detail table</strong>: Select a feature and verify the campaign-level
              breakdown looks correct
            </li>
          </>
        }
      />

      {config.featureIdsOverridden ? (
        // Sankey/raw-components/unmapped-features are actively misleading for a model
        // whose featurization was manually overridden (Sean, 2026-09-09) -- they'd
        // still reflect the normal pipeline's own declared inputs, not what actually
        // trained the model, so this replaces them entirely rather than appending a
        // caveat below content that shouldn't be trusted in the first place.
        <div className='data-qa-fm__override-notice'>
          <MdWarningAmber className='data-qa-fm__override-notice-icon' />
          <div>
            <div className='data-qa-fm__override-notice-title'>
              Featurization was manually overridden for this model
            </div>
            <p className='data-qa-fm__override-notice-body'>
              The normal campaign-taxonomy and feature-mapping pipeline was bypassed for this model,
              so the Sankey and raw-component breakdown this page would normally show aren&apos;t
              shown here — they would reflect that pipeline&apos;s own declared inputs, not what
              actually trained the model. Consult Econometrics if you need details on how features
              were constituted.
            </p>
          </div>
        </div>
      ) : (
        <>
          <MissingDeclaredFeaturesAlert
            missingDeclaredFeatures={config.missingDeclaredFeatures ?? []}
          />
          {/* Keyed on configPath -- FeatureMappingSection has no key tied to the active
              config, so ModelSwitcher switching models wouldn't otherwise remount the cards.
              Without it, the open cards and active tab, the MMM Feature dropdowns and
              RawComponentsTable's own filters (keyed only by feature name) could silently
              carry over whenever two models share a feature name -- remounting on model
              switch resets them instead. */}
          <FeatureMappingGroups
            key={config.configPath}
            config={config}
            initialFocus={initialFocus}
          />
        </>
      )}
    </div>
  );
};
